import { DatePipe } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { APP_TZ, toDisplayInstant } from '../../core/datetime/ist';
import { Complaint } from '../../core/models/complaint.model';
import { AuthService } from '../../core/services/auth.service';
import { ComplaintService } from '../../core/services/complaint.service';
import { HearingRecord, OpsService } from '../../core/services/ops.service';
import { RbacService } from '../../core/services/rbac.service';
import { DocumentOcrService } from '../document-ocr/document-ocr.service';

interface DocumentActivity {
  key: string;
  title: string;
  subtitle: string;
  state: 'Needs review' | 'Processing' | 'Case created' | 'Rejected' | 'Failed';
  updatedAt: string;
  path: string | any[];
}

type DashRole = string | null;
type ActivityFilter = 'All' | 'Complaint' | 'Workflow' | 'Hearing' | 'Resolution';

interface DashboardKpi {
  tone: string;
  icon: KpiIcon;
  label: string;
  value: string;
  path: string;
  queryParams?: Record<string, string>;
}

type KpiIcon = keyof typeof KPI_ICONS;

/* Stroke paths on a 24x24 grid, drawn with currentColor so a card's tone is the
   only thing that colours the glyph. Kept as data rather than markup so the
   template stays one <svg> instead of a switch over every icon. */
const KPI_ICONS = {
  doc: ['M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z', 'M14 3v5h5', 'M9 13h6', 'M9 17h4'],
  clock: ['M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z', 'M12 7.5V12l3 2'],
  check: ['M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z', 'm8.5 12.2 2.4 2.4 4.6-5'],
  alert: ['M10.3 4.3 2.8 17a2 2 0 0 0 1.7 3h15a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0z', 'M12 9.5v4', 'M12 17h.01'],
  reject: ['M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z', 'm9.5 9.5 5 5', 'm14.5 9.5-5 5'],
  calendar: ['M5 6h14a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1z', 'M4 10.5h16', 'M8.5 4v4', 'M15.5 4v4'],
  users: ['M16 20v-1.5a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4V20', 'M9.5 10.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z', 'M21 20v-1.5a4 4 0 0 0-3-3.9', 'M16.5 3.9a4 4 0 0 1 0 7.2'],
  building: ['M4 21V5a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v16', 'M15 10h4a1 1 0 0 1 1 1v10', 'M2 21h20', 'M8 8h3', 'M8 12h3', 'M8 16h3'],
  folder: ['M3 7.5A1.5 1.5 0 0 1 4.5 6h4L11 8.5h8.5A1.5 1.5 0 0 1 21 10v8a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18z'],
} as const;

interface DashboardActivity {
  id: string;
  at: string;
  kind: Exclude<ActivityFilter, 'All'>;
  title: string;
  body: string;
  reference?: string | number;
  path: string;
}

interface ChartShare {
  label: string;
  pct: number;
  color: string;
}

const CHART_COLORS = ['#0d9488', '#8b5cf6', '#3b82f6', '#f59e0b', '#ec4899', '#14b8a6'];

export function buildComplaintTypeShare(complaints: Complaint[]): ChartShare[] {
  const counts = new Map<string, number>();
  for (const complaint of complaints) {
    const type = complaint.category?.trim() || 'Other';
    counts.set(type, (counts.get(type) ?? 0) + 1);
  }

  const total = Math.max(complaints.length, 1);
  return [...counts.entries()]
    .sort(([labelA, countA], [labelB, countB]) =>
      countB - countA || labelA.localeCompare(labelB),
    )
    .map(([label, count], index) => ({
      label,
      pct: Math.round((count / total) * 100),
      color: CHART_COLORS[index % CHART_COLORS.length],
    }));
}

export function hearingCompletedCount(
  complaints: Complaint[],
  hearings: HearingRecord[],
): number {
  const heardCases = complaints.filter((complaint) => complaint.status === 'heard').length;
  const heldHearings = hearings.filter((hearing) => hearing.status === 'Completed').length;
  return Math.max(heardCases, heldHearings);
}

export function buildSixMonthTrend(
  complaints: Complaint[],
  now = new Date(),
): Array<{ key: string; label: string; value: number }> {
  const rows: Array<{ key: string; label: string; value: number }> = [];
  for (let offset = 5; offset >= 0; offset--) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - offset, 1));
    const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    rows.push({
      key,
      label: new Intl.DateTimeFormat('en-IN', { month: 'short', timeZone: APP_TZ }).format(d),
      value: 0,
    });
  }
  const byKey = new Map(rows.map((row) => [row.key, row]));
  for (const complaint of complaints) {
    const value = toDisplayInstant(complaint.submittedAt) || complaint.submittedAt;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) continue;
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: APP_TZ,
      year: 'numeric',
      month: '2-digit',
    }).formatToParts(d);
    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const row = year && month ? byKey.get(`${year}-${month}`) : undefined;
    if (row) row.value += 1;
  }
  return rows;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [RouterLink, DatePipe],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent {
  private readonly complaints = inject(ComplaintService);
  private readonly ops = inject(OpsService);
  private readonly rbac = inject(RbacService);
  private readonly auth = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly ocr = inject(DocumentOcrService);

  readonly role = computed((): DashRole => this.rbac.role());
  readonly isInvestigator = computed(() => this.rbac.isInvestigator());
  readonly officerName = computed(() => this.auth.officer()?.name ?? 'Officer');

  constructor() {
    // Records Officer + Admin dashboards include OCR intake KPIs/activity.
    const role = this.rbac.role();
    if (role === 'records_officer' || role === 'admin') void this.ocr.refresh();
    // Drop stale ?denied= from older redirects so the banner does not stick.
    if (this.route.snapshot.queryParamMap.get('denied')) {
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { denied: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  }

  readonly viewMode = signal<'table' | 'cards'>('table');
  readonly activityFilter = signal<ActivityFilter>('All');
  readonly refreshing = signal(false);
  readonly lastRefreshedAt = signal(new Date());
  readonly activityFilters: ActivityFilter[] = [
    'All',
    'Complaint',
    'Workflow',
    'Hearing',
    'Resolution',
  ];

  readonly canScheduling = computed(() => this.rbac.canModule('scheduling'));
  readonly canComplaints = computed(() => this.rbac.canModule('complaints'));
  readonly canCases = computed(() => this.rbac.canModule('cases'));
  readonly canResolution = computed(() => this.rbac.canModule('resolution'));
  readonly canRoster = computed(() => this.rbac.canModule('roster'));
  readonly canReports = computed(() => this.rbac.canModule('reports'));
  readonly canSettings = computed(() => this.rbac.canModule('settings'));

  private readonly deniedParam = toSignal(
    this.route.queryParamMap.pipe(map((p) => p.get('denied'))),
    { initialValue: null as string | null },
  );

  readonly deniedMessage = computed(() => {
    const denied = this.deniedParam();
    if (!denied) return null;
    return `Access denied to “${denied}” for your role (${this.role() ?? 'unknown'}).`;
  });

  readonly heading = computed(() => {
    const role = this.role();
    switch (role) {
      case 'dm':
        return { title: 'DM Dashboard' };
      case 'peshkar':
        return { title: 'Peshkar Dashboard' };
      case 'officer':
        return { title: 'Officer Dashboard' };
      case 'records_officer':
        return { title: 'Records Officer Dashboard' };
      case 'admin':
        return { title: 'Admin Dashboard' };
      default:
        if (this.isInvestigator()) {
          const label = role ? role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, ' ') : 'Desk';
          return { title: `${label} Dashboard` };
        }
        return { title: 'Dashboard' };
    }
  });

  /** Complaints scoped to this role’s view. */
  readonly scopedComplaints = computed(() => {
    const all = this.complaints.list();
    const myId = this.auth.officer()?.id;
    if (this.rbac.isAssigneeScoped()) {
      return all.filter(
        (c) => String(c.assignedOfficerRef ?? c.assignedRole ?? '') === String(myId ?? ''),
      );
    }
    return all;
  });

  readonly recent = computed(() => this.scopedComplaints().slice(0, 8));
  readonly hearings = this.ops.hearingList;
  readonly roster = this.ops.rosterList;
  readonly pendingResolutions = this.ops.pendingResolutionList;
  readonly users = this.ops.adminUsers;
  readonly notifications = this.ops.notificationList;

  readonly upcomingHearings = computed(() =>
    this.hearings()
      .filter((h) => h.status === 'Scheduled')
      .slice(0, 5),
  );

  readonly rosterAvailable = computed(() =>
    this.roster().filter((r) => r.availability === 'Available').length,
  );

  /** Intake-queue tallies backing the Records Officer's KPI row — mirrors the state
   *  classification on the Documents page (ocr-documents.component.ts) so the two agree. */
  readonly ocrCounts = computed(() => {
    const counts = { needsReview: 0, processing: 0, caseCreated: 0, rejected: 0, failed: 0 };
    for (const { batch, extractions } of this.ocr.batches()) {
      if (!extractions.length) {
        if (batch.status === 'failed') counts.failed += 1;
        else counts.processing += 1;
        continue;
      }
      for (const ext of extractions) {
        if (ext.matchStatus === 'rejected') counts.rejected += 1;
        else if (ext.grievanceRef != null) counts.caseCreated += 1;
        else counts.needsReview += 1;
      }
    }
    return counts;
  });

  /** Most recently updated intake items — the Records Officer's equivalent of the
   *  grievance-driven "Operational timeline" other roles see. */
  readonly recentDocuments = computed((): DocumentActivity[] => {
    const out: DocumentActivity[] = [];
    for (const { batch, extractions } of this.ocr.batches()) {
      if (!extractions.length) {
        out.push({
          key: `batch-${batch.id}`,
          title: `Batch #${batch.id}`,
          subtitle: batch.status === 'failed' ? (batch.errorMessage || 'Failed') : `Status: ${batch.status}`,
          state: batch.status === 'failed' ? 'Failed' : 'Processing',
          updatedAt: batch.createdAt,
          path: '/document-ocr/documents',
        });
        continue;
      }
      for (const ext of extractions) {
        const fields = ext.extractedJson;
        const state: DocumentActivity['state'] =
          ext.matchStatus === 'rejected' ? 'Rejected' : ext.grievanceRef != null ? 'Case created' : 'Needs review';
        out.push({
          key: `extraction-${ext.id}`,
          title: fields?.applicantName || `Case #${ext.id}`,
          subtitle: fields?.subjectEnglish || fields?.subjectHindi || '',
          state,
          updatedAt: ext.updatedAt,
          path: state === 'Needs review' ? ['/document-ocr', 'review', ext.id] : '/document-ocr/documents',
        });
      }
    }
    return out
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, 8);
  });

  readonly showDocumentActivity = computed(() => this.role() === 'records_officer');
  /** Admin: complaints + OCR recent lists in one tabbed card. */
  readonly showAdminRecentTabs = computed(() => this.role() === 'admin');
  readonly adminRecentTab = signal<'complaints' | 'documents'>('complaints');

  setAdminRecentTab(tab: 'complaints' | 'documents'): void {
    this.adminRecentTab.set(tab);
  }

  readonly kpis = computed(() => {
    const all = this.complaints.list();
    const mine = this.scopedComplaints();
    const byStatus = (rows: typeof all, ...keys: string[]) =>
      rows.filter((c) => keys.includes(c.status)).length;
    const hearings = this.hearings();
    const scheduled = hearings.filter((h) => h.status === 'Scheduled').length;
    const role = this.role();

    if (role === 'records_officer' || role === 'admin') {
      const c = this.ocrCounts();
      return <DashboardKpi[]>[
        { tone: 'amber', icon: 'alert', label: 'Needs review', value: String(c.needsReview), path: '/document-ocr/documents' },
        { tone: 'teal', icon: 'clock', label: 'Processing', value: String(c.processing), path: '/document-ocr/documents' },
        { tone: 'green', icon: 'check', label: 'Case created', value: String(c.caseCreated), path: '/document-ocr/documents' },
        { tone: 'rose', icon: 'reject', label: 'Rejected / failed', value: String(c.rejected + c.failed), path: '/document-ocr/documents' },
      ];
    }

    if (role === 'peshkar') {
      return <DashboardKpi[]>[
        { tone: 'gold', icon: 'calendar', label: 'Hearings scheduled', value: String(scheduled), path: '/scheduling' },
        { tone: 'teal', icon: 'users', label: 'Roster slots open', value: String(this.rosterAvailable()), path: '/roster' },
        { tone: 'amber', icon: 'folder', label: 'Cases to support', value: String(all.length), path: '/cases' },
        {
          tone: 'green',
          icon: 'check',
          label: 'Held / done',
          value: String(hearings.filter((h) => h.status === 'Completed').length),
          path: '/hearing-management',
          queryParams: { status: 'Completed' },
        },
      ];
    }

    if (role === 'officer' || this.isInvestigator()) {
      return <DashboardKpi[]>[
        { tone: 'teal', icon: 'doc', label: 'Assigned to me', value: String(mine.length), path: '/complaints' },
        {
          tone: 'amber',
          icon: 'clock',
          label: 'In progress',
          value: String(byStatus(mine, 'routed', 'under_review', 'scheduled', 'heard')),
          path: '/cases',
        },
        {
          tone: 'rose',
          icon: 'alert',
          label: 'Awaiting my report',
          value: String(byStatus(mine, 'routed')),
          path: '/complaints',
          queryParams: { status: 'routed' },
        },
        {
          tone: 'green',
          icon: 'check',
          label: 'Resolved by me',
          value: String(byStatus(mine, 'resolved', 'closed')),
          path: '/complaints',
          queryParams: { status: 'resolved' },
        },
      ];
    }

    // DM — only total complaints + total departments
    return <DashboardKpi[]>[
      {
        tone: 'teal',
        icon: 'doc',
        label: 'Total complaints',
        value: String(all.length),
        path: '/complaints',
      },
      {
        tone: 'navy',
        icon: 'building',
        label: 'Total departments',
        value: String(this.ops.departments.length),
        path: '/complaints',
      },
    ];
  });

  iconPaths(icon: KpiIcon): readonly string[] {
    return KPI_ICONS[icon];
  }

  readonly primaryActions = computed(() => {
    const role = this.role();
    if (role === 'peshkar') {
      return [
        { label: 'Schedule hearing', path: '/scheduling', primary: true },
        { label: 'Roster', path: '/roster', primary: false },
        { label: 'Cases', path: '/cases', primary: false },
      ];
    }
    if (role === 'officer' || this.isInvestigator()) {
      return [
        { label: 'My complaints', path: '/complaints', primary: true },
        { label: 'Case management', path: '/cases', primary: false },
      ];
    }
    if (role === 'admin') {
      return [
        { label: 'Complaint management', path: '/complaints', primary: true },
        { label: 'Document OCR', path: '/document-ocr', primary: false },
        { label: 'User management', path: '/settings', primary: false },
      ];
    }
    if (role === 'records_officer') {
      return [{ label: 'Document OCR', path: '/document-ocr', primary: true }];
    }
    // DM — lean dashboard; KPIs + Ask AI bot
    return [
      { label: 'Complaint management', path: '/complaints', primary: true },
      { label: 'Ask AI', path: '/assistant', primary: false },
    ];
  });

  /** Executive charts for DM + Admin (mirrors DM). */
  readonly showAnalytics = computed(() => this.role() === 'dm' || this.role() === 'admin');
  readonly showHearingsPanel = computed(() => this.role() === 'peshkar');
  readonly showRosterPanel = computed(() => this.role() === 'peshkar');
  readonly showComplaintsPanel = computed(() =>
    this.role() === 'dm' || this.isInvestigator(),
  );
  readonly showResolutionPanel = computed(() => false);
  readonly showActivityFeed = computed(
    () => this.role() !== 'dm' && this.role() !== 'admin' && this.role() !== 'records_officer',
  );

  readonly statusShare = computed(() => {
    const rows = this.scopedComplaints();
    const total = Math.max(rows.length, 1);
    const n = (...keys: string[]) => rows.filter((c) => keys.includes(c.status)).length;
    return [
      { label: 'Resolved', pct: Math.round((n('resolved', 'closed') / total) * 100), color: '#0d9488' },
      { label: 'Hearing done', pct: Math.round((n('heard') / total) * 100), color: '#8b5cf6' },
      { label: 'Active', pct: Math.round((n('routed', 'investigation_completed', 'awaiting_hearing', 'scheduled', 'reopened') / total) * 100), color: '#3b82f6' },
      { label: 'Pending', pct: Math.round((n('submitted', 'pending_verification', 'under_review') / total) * 100), color: '#f59e0b' },
    ];
  });

  readonly complaintTypeShare = computed(() =>
    buildComplaintTypeShare(this.scopedComplaints()),
  );

  readonly donutShare = computed(() =>
    this.role() === 'dm' || this.role() === 'admin'
      ? this.complaintTypeShare()
      : this.statusShare(),
  );

  readonly donutGradient = computed(() => {
    const rows = this.donutShare();
    let cursor = 0;
    const parts: string[] = [];
    for (const row of rows) {
      const next = cursor + row.pct;
      parts.push(`${row.color} ${cursor}% ${next}%`);
      cursor = next;
    }
    if (cursor < 100) parts.push(`var(--line) ${cursor}% 100%`);
    return `conic-gradient(${parts.join(', ')})`;
  });

  /** Live six-month intake trend derived from grievance submittedAt timestamps. */
  readonly monthTrend = computed(() => buildSixMonthTrend(this.scopedComplaints()));

  readonly trendMax = computed(() =>
    Math.max(1, ...this.monthTrend().map((row) => row.value)),
  );

  /** Open vs. resolved grievances per department — real counts from live grievance records. */
  readonly departmentBreakdown = computed(() => {
    const rows = this.scopedComplaints();
    const DONE = new Set(['resolved', 'closed']);
    const map = new Map<string, { department: string; open: number; resolved: number }>();
    for (const c of rows) {
      const dept = c.department || 'Unassigned';
      const entry = map.get(dept) ?? { department: dept, open: 0, resolved: 0 };
      if (DONE.has(c.status)) entry.resolved += 1;
      else entry.open += 1;
      map.set(dept, entry);
    }
    return [...map.values()].sort((a, b) => b.open + b.resolved - (a.open + a.resolved));
  });

  readonly departmentMax = computed(() =>
    Math.max(1, ...this.departmentBreakdown().map((d) => d.open + d.resolved)),
  );

  departmentBarWidth(value: number): number {
    return Math.round((value / this.departmentMax()) * 100);
  }

  readonly activityFeed = computed(() => {
    const events: DashboardActivity[] = [];
    const hearingPath = this.rbac.canModule('hearingManagement')
      ? '/hearing-management'
      : this.rbac.canModule('scheduling')
        ? '/scheduling'
        : '/complaints';
    const resolutionPath = this.rbac.canModule('resolution') ? '/resolution' : '/complaints';
    const workflowPath = this.rbac.canModule('workflow') ? '/workflow' : '/complaints';

    for (const n of this.notifications()) {
      events.push({
        id: `notification-${n.id}`,
        at: n.at,
        kind:
          n.type === 'Hearing'
            ? 'Hearing'
            : n.type === 'Resolution'
              ? 'Resolution'
              : 'Workflow',
        title: n.title,
        body: n.body,
        reference: n.grievanceRef,
        path:
          n.type === 'Hearing'
            ? hearingPath
            : n.type === 'Resolution'
              ? resolutionPath
              : workflowPath,
      });
    }

    for (const h of this.hearings()) {
      events.push({
        id: `hearing-${h.id}-${h.status}`,
        at: h.scheduledAt,
        kind: 'Hearing',
        title: h.status === 'Completed' ? 'Hearing completed' : `Hearing ${h.status.toLowerCase()}`,
        body: `${h.caseNo} · ${h.subject}`,
        reference: h.caseNo,
        path: hearingPath,
      });
    }

    for (const c of this.scopedComplaints()) {
      const event = this.complaintActivity(c);
      events.push(event);
    }

    const unique = new Map<string, DashboardActivity>();
    for (const event of events) {
      const minute = String(event.at || '').slice(0, 16);
      const key = `${event.kind}|${event.reference || ''}|${minute}|${event.title}`;
      if (!unique.has(key)) unique.set(key, event);
    }

    return [...unique.values()]
      .filter((event) => this.activityFilter() === 'All' || event.kind === this.activityFilter())
      .sort((a, b) => this.eventTime(b.at) - this.eventTime(a.at))
      .slice(0, 16);
  });

  setView(mode: 'table' | 'cards'): void {
    this.viewMode.set(mode);
  }

  setActivityFilter(filter: ActivityFilter): void {
    this.activityFilter.set(filter);
  }

  async refreshDashboard(): Promise<void> {
    if (this.refreshing()) return;
    this.refreshing.set(true);
    try {
      await this.ops.refresh();
      this.lastRefreshedAt.set(new Date());
    } finally {
      this.refreshing.set(false);
    }
  }

  trendHeight(value: number): number {
    if (value === 0) return 4;
    return Math.max(10, Math.round((value / this.trendMax()) * 100));
  }

  private complaintActivity(complaint: Complaint): DashboardActivity {
    const status = complaint.status;
    const kind: DashboardActivity['kind'] =
      status === 'heard' || status === 'scheduled' || status === 'awaiting_hearing'
        ? 'Hearing'
        : status === 'resolved' || status === 'closed'
          ? 'Resolution'
          : status === 'submitted'
            ? 'Complaint'
            : 'Workflow';
    return {
      id: `complaint-${complaint.id}-${status}`,
      at: complaint.updatedAt || complaint.submittedAt,
      kind,
      title: this.statusTitle(status),
      body: `${complaint.trackingId || complaint.id} · ${complaint.subject || complaint.category}`,
      reference: complaint.id,
      path: this.canComplaints() ? `/complaints/${complaint.id}` : '/dashboard',
    };
  }

  private statusTitle(status: string): string {
    const labels: Record<string, string> = {
      submitted: 'Grievance submitted',
      pending_verification: 'Verification pending',
      under_review: 'Grievance under review',
      routed: 'Assigned for investigation',
      investigation_completed: 'Investigation completed',
      awaiting_hearing: 'Hearing requested',
      scheduled: 'Hearing scheduled',
      heard: 'Hearing completed',
      resolved: 'Grievance resolved',
      closed: 'Case closed',
      rejected: 'Grievance rejected',
      reopened: 'Case reopened',
    };
    return labels[status] || status.replace(/_/g, ' ');
  }

  private eventTime(value: string): number {
    const normalized = toDisplayInstant(value) || value;
    const time = new Date(normalized).getTime();
    return Number.isNaN(time) ? 0 : time;
  }
}
